import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Grid, Table, Image, Checkbox, Icon, Popup, Radio, Dropdown, Card } from "semantic-ui-react";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { ensureHttpsImageUrl, dateFormatDeadline, convertAddressFull } from "../../app/common/util/util";
import { getDocs, onSnapshot } from "firebase/firestore";
import { dataFromSnapshot, fetchAllTasksFromDb, fetchNotificationsForUserFromDb, fetchDocsWithUnresolvedCommentsFromDb } from "../../app/firestore/firestoreService";
import { openModal } from "../../app/common/modals/modalSlice";
import { useMediaQuery } from "react-responsive";
import { format } from "date-fns";
import { deadlineMilestonesMapping } from "../deadlineTemplates/deadlineTemplatesComponents/DeadlineTemplatesFormFieldMapping";
import { buildWeeklyDeadlineSuggestions, shouldShowWeeklyDeadlineSuggestions } from "../notifications/weeklyDeadlineEmail";

export default function AssistDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState("Tasks");
  const [showCompletedTasks, setShowCompletedTasks] = useState(true);
  const [showInProgressTasks, setShowInProgressTasks] = useState(true);
  const [showTasksWithDeadlines, setShowTasksWithDeadlines] = useState(true);
  const [showTasksWithoutDeadlines, setShowTasksWithoutDeadlines] = useState(true);
  const [showBuyers, setShowBuyers] = useState(true);
  const [showSellers, setShowSellers] = useState(true);
  const [showAssignedTasks] = useState(true);
  const [showUnassigned, setShowUnassigned] = useState(true);
  const [showAssignedToMe, setShowAssignedToMe] = useState(true);
  const [showAssignedToOthers, setShowAssignedToOthers] = useState(true);
  const [allTasks, setAllTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [milestonesViewMode, setMilestonesViewMode] = useState("Overview");
  const [expandedTransactionId, setExpandedTransactionId] = useState(null);
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });

  const { currentUserProfile } = useSelector((state) => state.profile);
  const { transActive, transActiveForManager } = useSelector((state) => state.transaction);
  const { tasksAllWithDateUpcoming } = useSelector((state) => state.task);
  const [notifications, setNotifications] = useState([]);
  const [docsWithComments, setDocsWithComments] = useState([]);
  const currentUserProfileId = currentUserProfile?.id;

  // Use transActiveForManager for managers and manager assistants
  const isManagerOrAssistant = currentUserProfile?.role === "manager" || currentUserProfile?.role === "managerassistant";
  const transactions = isManagerOrAssistant ? transActiveForManager : transActive;

  // Filter transactions based on showBuyers and showSellers, then sort by closing date
  const filteredTransactions = useMemo(() => {
    if (!transactions) return [];
    const filtered = transactions.filter((transaction) => {
      if (!showBuyers && transaction.agentRepresents === "Buyer") return false;
      if (!showSellers && transaction.agentRepresents === "Seller") return false;
      return true;
    });
    // Sort by closing date: transactions with closing date first, then those without
    return filtered.sort((a, b) => {
      const aDate = a.closingDateTime?.toDate?.() || a.closingDateTime;
      const bDate = b.closingDateTime?.toDate?.() || b.closingDateTime;
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1; // a has no date, move to end
      if (!bDate) return -1; // b has no date, move to end
      return new Date(aDate) - new Date(bDate); // Sort by date ascending
    });
  }, [transactions, showBuyers, showSellers]);

  // Navigate to transaction overview
  const goToTransaction = (transactionId) => {
    navigate(`/transactions/${transactionId}/overview`);
  };

  useEffect(() => {
    if (!currentUserProfileId) return;

    const unsubscribe = onSnapshot(
      fetchNotificationsForUserFromDb(currentUserProfileId),
      (snapshot) => {
        const notifs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setNotifications(notifs);
      },
      (error) => {
        console.error("Error fetching notifications:", error);
      }
    );

    return () => unsubscribe();
  }, [currentUserProfileId]);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      fetchDocsWithUnresolvedCommentsFromDb(),
      (snapshot) => {
        const docs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          _isDocumentComment: true,
        }));
        setDocsWithComments(docs);
      },
      (error) => {
        console.error("Error fetching documents with unresolved comments:", error);
      }
    );

    return () => unsubscribe();
  }, []);

  // Refetch tasks helper function
  const refetchTasks = async () => {
    if (!transactions || transactions.length === 0) {
      return;
    }
    try {
      const snapshot = await getDocs(fetchAllTasksFromDb());
      const tasks = snapshot.docs.map((doc) => dataFromSnapshot(doc));
      const activeTransactionIds = new Set(transactions.map(t => t.id));
      const filteredTasks = tasks.filter(
        task => activeTransactionIds.has(task.transactionId) && !task.archived
      );
      setAllTasks(filteredTasks);
    } catch (error) {
      console.error("Error refetching tasks:", error);
    }
  };

  // Handle task click to open edit modal
  const handleTaskClick = (task) => {
    dispatch(
      openModal({
        modalType: "TaskEditSimple",
        modalProps: { task, onSave: refetchTasks },
      })
    );
  };

  // Fetch all tasks for active transactions
  useEffect(() => {
    let isMounted = true;

    async function fetchAllTasks() {
      if (!transactions || transactions.length === 0) {
        if (isMounted) {
          setLoadingTasks(false);
        }
        return;
      }

      try {
        if (isMounted) {
          setLoadingTasks(true);
        }
        const snapshot = await getDocs(fetchAllTasksFromDb());
        const tasks = snapshot.docs.map((doc) => dataFromSnapshot(doc));

        // Filter to only tasks for active transactions and not archived
        const activeTransactionIds = new Set(transactions.map(t => t.id));
        const filteredTasks = tasks.filter(
          task => activeTransactionIds.has(task.transactionId) && !task.archived
        );

        if (isMounted) {
          setAllTasks(filteredTasks);
          setLoadingTasks(false);
        }
      } catch (error) {
        console.error("Error fetching tasks:", error);
        if (isMounted) {
          setLoadingTasks(false);
        }
      }
    }

    fetchAllTasks();

    return () => {
      isMounted = false;
    };
  }, [transactions]);

  // Group tasks by transaction
  const tasksByTransaction = useMemo(() => {
    const grouped = {};
    allTasks.forEach((task) => {
      if (!grouped[task.transactionId]) {
        grouped[task.transactionId] = [];
      }
      grouped[task.transactionId].push(task);
    });
    return grouped;
  }, [allTasks]);

  // Extract current user ID for dependency tracking
  const currentUserId = currentUserProfile?.userId;

  // Filter and sort tasks for each transaction based on filter checkboxes
  const filteredTasksByTransaction = useMemo(() => {
    const filtered = {};
    Object.keys(tasksByTransaction).forEach((transactionId) => {
      const tasks = tasksByTransaction[transactionId].filter((task) => {
        // Filter out completed tasks if showCompletedTasks is false
        if (!showCompletedTasks && task.status === "Complete") return false;
        // Filter out in progress tasks if showInProgressTasks is false
        if (!showInProgressTasks && task.inProgress) return false;
        // Filter out tasks with deadlines if showTasksWithDeadlines is false
        if (!showTasksWithDeadlines && task.end) return false;
        // Filter out tasks without deadlines if showTasksWithoutDeadlines is false
        if (!showTasksWithoutDeadlines && !task.end) return false;
        // Filter out assigned tasks if showAssignments is false
        const hasAssignment = Array.isArray(task.assignedTo) && task.assignedTo.length > 0;
        if (!showAssignedTasks && hasAssignment) return false;
        // Filter out unassigned tasks if showUnassigned is false
        if (!showUnassigned && !hasAssignment) return false;
        // Filter by "Assigned to Me" and "Assigned to Others"
        if (hasAssignment && currentUserId) {
          const isAssignedToMe = task.assignedTo.includes(currentUserId);
          const isAssignedToOthers = task.assignedTo.some(id => id !== currentUserId);
          // If task is only assigned to me and showAssignedToMe is false, filter out
          if (isAssignedToMe && !isAssignedToOthers && !showAssignedToMe) return false;
          // If task is only assigned to others and showAssignedToOthers is false, filter out
          if (!isAssignedToMe && isAssignedToOthers && !showAssignedToOthers) return false;
          // If task is assigned to both me and others, show if either filter is enabled
          if (isAssignedToMe && isAssignedToOthers && !showAssignedToMe && !showAssignedToOthers) return false;
        }
        return true;
      });

      // Sort: tasks with deadlines first (sorted by date), then tasks without deadlines
      filtered[transactionId] = tasks.sort((a, b) => {
        const aHasDeadline = !!a.end;
        const bHasDeadline = !!b.end;

        if (aHasDeadline && !bHasDeadline) return -1;
        if (!aHasDeadline && bHasDeadline) return 1;
        if (aHasDeadline && bHasDeadline) {
          return a.end - b.end; // Sort by date ascending
        }
        return 0;
      });
    });
    return filtered;
  }, [tasksByTransaction, showCompletedTasks, showInProgressTasks, showTasksWithDeadlines, showTasksWithoutDeadlines, showAssignedTasks, showUnassigned, showAssignedToMe, showAssignedToOthers, currentUserId]);

  // Get the maximum number of tasks for any transaction to determine row count
  const maxTaskCount = useMemo(() => {
    return Math.max(
      0,
      ...Object.values(filteredTasksByTransaction).map((tasks) => tasks.length)
    );
  }, [filteredTasksByTransaction]);

  if (!transactions || loadingTasks) {
    return <LoadingComponent content="Loading transactions..." />;
  }

  const renderTasksView = () => {
    return (
      <div>
        {isMobile ? (
          <div style={{ marginBottom: "20px" }}>
            <b>Mobile View</b> - Use desktop for best experience.
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: "20px" }}>
              Active Transactions, showing tasks with deadlines first, then
              tasks without deadlines.
              <Popup
                trigger={
                  <Icon
                    name="info circle"
                    color="blue"
                    style={{ marginLeft: "4px", cursor: "pointer" }}
                  />
                }
                content={
                  <p>
                    You can click on the transaction information to go to the
                    transaction's Overview page.
                    <br />
                    You can use the checkboxes to filter the tasks shown.
                    <br />
                    You can click on a task to edit it.
                    <br />
                    Tasks: Yellow = In Progress, Red = Past Due, Green =
                    Completed
                  </p>
                }
                position="top center"
                size="small"
              />
              <h3>Select options to filter view</h3>
              <Grid columns={4} style={{ marginTop: "10px" }}>
                <Grid.Column width={3}>
                  <b>Task Status</b>
                  <br />
                  <Checkbox
                    label="Show completed tasks"
                    checked={showCompletedTasks}
                    onChange={(_, { checked }) =>
                      setShowCompletedTasks(checked)
                    }
                  />
                  <br />
                  <Checkbox
                    label="Show in progress tasks"
                    checked={showInProgressTasks}
                    onChange={(_, { checked }) =>
                      setShowInProgressTasks(checked)
                    }
                    style={{ marginTop: "5px" }}
                  />
                </Grid.Column>
                <Grid.Column width={3}>
                  <b>Deadlines or ToDos</b>
                  <br />
                  <Checkbox
                    label="Tasks with deadlines"
                    checked={showTasksWithDeadlines}
                    onChange={(_, { checked }) =>
                      setShowTasksWithDeadlines(checked)
                    }
                  />
                  <br />
                  <Checkbox
                    label="Tasks without deadlines"
                    checked={showTasksWithoutDeadlines}
                    onChange={(_, { checked }) =>
                      setShowTasksWithoutDeadlines(checked)
                    }
                    style={{ marginTop: "5px" }}
                  />
                </Grid.Column>
                <Grid.Column width={2}>
                  <b>Representation</b>
                  <br />
                  <Checkbox
                    label="Buyers"
                    checked={showBuyers}
                    onChange={(_, { checked }) => setShowBuyers(checked)}
                  />
                  <br />
                  <Checkbox
                    label="Sellers"
                    checked={showSellers}
                    onChange={(_, { checked }) => setShowSellers(checked)}
                    style={{ marginTop: "5px" }}
                  />
                </Grid.Column>
                <Grid.Column width={3}>
                  <b>Task Assignments</b>
                  <br />
                  <Checkbox
                    label="Assigned to Me"
                    checked={showAssignedToMe}
                    onChange={(_, { checked }) => setShowAssignedToMe(checked)}
                  />
                  <br />
                  <Checkbox
                    label="Assigned to Others"
                    checked={showAssignedToOthers}
                    onChange={(_, { checked }) =>
                      setShowAssignedToOthers(checked)
                    }
                    style={{ marginTop: "5px" }}
                  />
                  {/* <br />
              <Checkbox
                label="Show All Assigned Tasks"
                checked={showAssignedTasks}
                onChange={(_, { checked }) => setShowAssignedTasks(checked)}
              /> */}
                  <br />
                  <Checkbox
                    label="Unassigned Tasks"
                    checked={showUnassigned}
                    onChange={(_, { checked }) => setShowUnassigned(checked)}
                    style={{ marginTop: "5px" }}
                  />
                </Grid.Column>
                {/* <Grid.Column width={3}>
              <b>Misc</b>
              <br />
              
            </Grid.Column> */}
              </Grid>
            </div>
            {!filteredTransactions ||
            filteredTransactions.length === 0 ||
            !filteredTasksByTransaction ||
            Object.keys(filteredTasksByTransaction).length === 0 ? (
              <p>
                No transactions with tasks to display.
                <br />
                Adjust your filters to show more transactions.
              </p>
            ) : (
              <div
                style={{
                  overflowX: "auto",
                  overflowY: "auto",
                  maxHeight: "80vh",
                }}
              >
                <Table celled structured>
                  <Table.Header>
                    <Table.Row
                      className="0 margin top 0 margin bottom 0 padding"
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                        background: "white",
                      }}
                    >
                      <Table.HeaderCell
                        style={{
                          minWidth: "140px",
                          margin: "0px",
                          position: "sticky",
                          top: 0,
                          background: "white",
                        }}
                      >
                        Transaction Info
                      </Table.HeaderCell>
                      {filteredTransactions.map((transaction, index) => (
                        <Table.HeaderCell
                          key={transaction.id + index}
                          style={{
                            minWidth: "180px",
                            textAlign: "center",
                            fontSize: "9pt",
                            margin: "0px",
                            padding: "2px",
                            position: "sticky",
                            top: 0,
                            background: "white",
                          }}
                          onClick={() => goToTransaction(transaction.id)}
                        >
                          {transaction.title}
                        </Table.HeaderCell>
                      ))}
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {/* Thumbnail Row - Sticky */}
                    <Table.Row
                      style={{
                        position: "sticky",
                        top: "41px",
                        zIndex: 2,
                        background: "gray",
                      }}
                    >
                      <Table.Cell
                        style={{
                          position: "sticky",
                          top: "41px",
                          background: "white",
                        }}
                      >
                        <strong>Photo</strong>
                      </Table.Cell>
                      {filteredTransactions.map((transaction, index) => (
                        <Table.Cell
                          key={transaction.id + index}
                          style={{
                            textAlign: "center",
                            cursor: "pointer",
                            position: "sticky",
                            top: "41px",
                            background: "white",
                          }}
                          onClick={() => goToTransaction(transaction.id)}
                        >
                          {transaction.pic ? (
                            <Image
                              src={ensureHttpsImageUrl(transaction.pic)}
                              size="tiny"
                              centered
                            />
                          ) : (
                            <Image
                              src="/assets/placeholder-house.png"
                              size="tiny"
                              centered
                            />
                          )}
                        </Table.Cell>
                      ))}
                    </Table.Row>

                    {/* Street Address Row - Sticky */}
                    <Table.Row
                      style={{
                        position: "sticky",
                        top: "121px",
                        zIndex: 1,
                        background: "white",
                      }}
                    >
                      <Table.Cell
                        style={{
                          position: "sticky",
                          top: "121px",
                          background: "white",
                        }}
                      >
                        <strong>Street Address</strong>
                      </Table.Cell>
                      {filteredTransactions.map((transaction, index) => (
                        <Table.Cell
                          key={transaction.id + index}
                          style={{
                            textAlign: "center",
                            cursor: "pointer",
                            position: "sticky",
                            top: "121px",
                            background: "white",
                            margin: "4px",
                            padding: "4px",
                          }}
                          onClick={() => goToTransaction(transaction.id)}
                        >
                          {transaction.address?.street || "N/A"}
                        </Table.Cell>
                      ))}
                    </Table.Row>

                    {/* City Row */}
                    <Table.Row className="tiny margin top tiny margin bottom">
                      <Table.Cell>
                        <strong>City</strong>
                      </Table.Cell>
                      {filteredTransactions.map((transaction, index) => (
                        <Table.Cell
                          key={transaction.id + index}
                          style={{ textAlign: "center" }}
                          onClick={() => goToTransaction(transaction.id)}
                        >
                          {transaction.address?.city || "N/A"}
                        </Table.Cell>
                      ))}
                    </Table.Row>

                    {/* Buyer or Seller Row */}
                    <Table.Row className="tiny margin top tiny margin bottom">
                      <Table.Cell>
                        <strong>Buyer or Seller</strong>
                      </Table.Cell>
                      {filteredTransactions.map((transaction, index) => (
                        <Table.Cell
                          key={transaction.id + index}
                          style={{ textAlign: "center" }}
                          onClick={() => goToTransaction(transaction.id)}
                        >
                          {transaction.agentRepresents || "N/A"}
                        </Table.Cell>
                      ))}
                    </Table.Row>

                    {/* Closing Date Row */}
                    <Table.Row className="tiny margin top tiny margin bottom">
                      <Table.Cell>
                        <strong>Closing Date</strong>
                      </Table.Cell>
                      {filteredTransactions.map((transaction, index) => (
                        <Table.Cell
                          key={transaction.id + index}
                          style={{ textAlign: "center" }}
                          onClick={() => goToTransaction(transaction.id)}
                        >
                          {(transaction?.closingDateTime &&
                            format(
                              transaction.closingDateTime,
                              "MM/dd/yyyy"
                            )) ||
                            "N/A"}
                        </Table.Cell>
                      ))}
                    </Table.Row>

                    {/* Task Rows - one row per task index */}
                    {Array.from({ length: maxTaskCount }).map(
                      (_, taskIndex) => (
                        <Table.Row
                          key={taskIndex}
                          className="tiny margin top tiny margin bottom"
                        >
                          <Table.Cell>
                            {/* <strong>Task {taskIndex + 1}</strong> */}
                          </Table.Cell>
                          {filteredTransactions.map((transaction, index) => {
                            const transactionTasks =
                              filteredTasksByTransaction[transaction.id] || [];
                            const task = transactionTasks[taskIndex];

                            if (!task) {
                              return (
                                <Table.Cell
                                  key={transaction.id + index}
                                  style={{
                                    textAlign: "center",
                                    backgroundColor: "#aaa",
                                  }}
                                >
                                  -
                                </Table.Cell>
                              );
                            }

                            const isComplete = task.status === "Complete";
                            const inProgress = task.inProgress;
                            const taskEndDate =
                              task.end?.toDate?.() || task.end;
                            const isPastDue =
                              taskEndDate &&
                              new Date(taskEndDate) < new Date() &&
                              !isComplete;

                            return (
                              <Table.Cell
                                key={transaction.id + index}
                                style={{
                                  textAlign: "left",
                                  backgroundColor: isPastDue
                                    ? "#f8d7da"
                                    : isComplete
                                    ? "#d4edda"
                                    : inProgress
                                    ? "#fff8db"
                                    : "transparent",
                                  cursor: "pointer",
                                  fontSize: "9pt",
                                  margin: "2px",
                                  padding: "0px",
                                  lineHeight: "1.1em",
                                }}
                                onClick={() => handleTaskClick(task)}
                              >
                                <div
                                  style={{
                                    fontWeight: "500",
                                    margin: "3px",
                                    padding: "0px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <span>{task.title}</span>
                                  {task.description && (
                                    <Popup
                                      trigger={
                                        <Icon
                                          name="info circle"
                                          color="blue"
                                          style={{
                                            marginLeft: "4px",
                                            cursor: "pointer",
                                          }}
                                        />
                                      }
                                      content={task.description}
                                      position="top center"
                                      size="small"
                                    />
                                  )}
                                </div>
                                {task.end && (
                                  <div
                                    style={{
                                      fontSize: "0.9em",
                                      color: "#666",
                                      margin: "3px",
                                      textAlign: "right",
                                    }}
                                  >
                                    {dateFormatDeadline(task.end)}
                                  </div>
                                )}
                              </Table.Cell>
                            );
                          })}
                        </Table.Row>
                      )
                    )}
                  </Table.Body>
                </Table>
              </div>
            )}
          </div>
        )}
      </div>
    );
};

  const renderMilestonesView = () => {
    // Get under contract transactions
    const underContractTransactions = filteredTransactions.filter(t => t.status === "Under Contract");

    // All deadline names from mapping
    const allDeadlineNames = Object.keys(deadlineMilestonesMapping);

    // Define grouped deadlines for Overview mode
    const overviewGroups = {
      "Title": [
        "Record Title Deadline (and Tax Certificate)",
        "Record Title Objection Deadline",
        "Off-Record Title Deadline",
        "Off-Record Title Objection Deadline",
        "Title Resolution Deadline"
      ],
      "ILC/Survey": [
        "New ILC or New Survey Deadline",
        "New ILC or New Survey Objection Deadline",
        "New ILC or New Survey Resolution Deadline"
      ],
      "Appraisal": [
        "Appraisal Deadline",
        "Appraisal Objection Deadline",
        "Appraisal Resolution Deadline"
      ],
      "Inspection": [
        "Inspection Termination Deadline",
        "Inspection Objection Deadline",
        "Inspection Resolution Deadline",
        "Water Rights Examination Deadline",
        "Mineral Rights Examination Deadline"
      ],
      "Loan": [
        "New Loan Application Deadline",
        "New Loan Terms Deadline",
        "New Loan Availability Deadline",
        "Existing Loan Deadline",
        "Existing Loan Termination Deadline",
        "Loan Transfer Approval Deadline"
      ],
      "Due Diligence": [
        "Due Diligence Documents Delivery Deadline",
        "Due Diligence Documents Objection Deadline",
        "Due Diligence Documents Resolution Deadline"
      ],
      "Association": [
        "Association Documents Deadline",
        "Association Documents Termination Deadline"
      ],
      "Closing": [
        "Closing Date",
        "Possession Date"
      ]
    };

    // Determine which deadlines to show based on view mode
    const deadlinesToShow = milestonesViewMode === "Overview"
      ? Object.keys(overviewGroups)
      : allDeadlineNames;

    // Helper function to check if a deadline task exists and has passed
    const getDeadlineStatus = (transaction, deadlineName) => {
      const transactionTasks = tasksByTransaction[transaction.id] || [];

      // For overview mode, check if any deadline in the group has passed
      if (milestonesViewMode === "Overview") {
        const groupDeadlines = overviewGroups[deadlineName] || [];
        let hasAnyDeadline = false;
        let anyPassed = false;

        groupDeadlines.forEach(dl => {
          const task = transactionTasks.find(t => t.title === dl);
          if (task && task.end) {
            hasAnyDeadline = true;
            const taskEndDate = task.end?.toDate?.() || task.end;
            if (new Date(taskEndDate) < new Date()) {
              anyPassed = true;
            }
          }
        });

        if (!hasAnyDeadline) return "none";
        return anyPassed ? "passed" : "pending";
      } else {
        // Detailed mode - check individual deadline
        const task = transactionTasks.find(t => t.title === deadlineName);
        if (!task || !task.end) return "none";
        const taskEndDate = task.end?.toDate?.() || task.end;
        return new Date(taskEndDate) < new Date() ? "passed" : "pending";
      }
    };

    return (
      <div style={{ textAlign: "left", padding: isMobile ? "10px" : "10px" }}>
        <h2>Milestones</h2>
        <p>View milestone completions for your under contract transactions.</p>
        <div style={{ marginBottom: "20px" }}>
          <Radio
            label="Overview"
            name="milestonesViewMode"
            value="Overview"
            checked={milestonesViewMode === "Overview"}
            onChange={() => setMilestonesViewMode("Overview")}
            style={{ marginRight: "20px" }}
          />
          <Radio
            label="Detailed"
            name="milestonesViewMode"
            value="Detailed"
            checked={milestonesViewMode === "Detailed"}
            onChange={() => setMilestonesViewMode("Detailed")}
          />
        </div>

        {underContractTransactions.length === 0 ? (
          <p style={{ color: "#888" }}>
            No transactions currently under contract.
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table celled compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    style={{
                      position: "sticky",
                      left: 0,
                      background: "white",
                      zIndex: 1,
                    }}
                  >
                    Transaction
                  </Table.HeaderCell>
                  {deadlinesToShow.map((deadline, idx) => (
                    <Table.HeaderCell
                      key={idx}
                      style={{ whiteSpace: "wrap", fontSize: "0.85em" }}
                    >
                      {deadline}
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {underContractTransactions.map((transaction, index) => (
                  <Table.Row key={transaction.id + index}>
                    <Table.Cell
                      style={{
                        position: "sticky",
                        left: 0,
                        background: "white",
                        zIndex: 1,
                        cursor: "pointer",
                        fontWeight: "500",
                        minWidth: "170px",
                        width: "170px",
                      }}
                      onClick={() => goToTransaction(transaction.id)}
                    >
                      {transaction.title}
                    </Table.Cell>
                    {deadlinesToShow.map((deadline, idx) => {
                      const status = getDeadlineStatus(transaction, deadline);
                      const backgroundColor =
                        status === "passed"
                          ? "#d4edda"
                          : status === "pending"
                          ? "#fff8db"
                          : "#eee";
                      return (
                        <Table.Cell
                          key={idx}
                          style={{
                            textAlign: "center",
                            backgroundColor: backgroundColor,
                          }}
                        >
                          {status === "passed" && (
                            <Icon name="check" color="green" />
                          )}
                          {status === "pending" && (
                            <Icon name="clock outline" color="blue" />
                          )}
                          {status === "none" && (
                            <span style={{ color: "#aaa" }}>—</span>
                          )}
                        </Table.Cell>
                      );
                    })}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>
    );
  };

  const renderSuggestionsView = () => {
    // Helper to check if MLS number is missing
    const isMlsMissing = (t) => {
      const hasMainMls = t && t.mlsNum && t.mlsNum.length > 0;
      const hasMlsNumbers =
        t &&
        t.mlsNumbers &&
        Array.isArray(t.mlsNumbers) &&
        t.mlsNumbers.length > 0 &&
        t.mlsNumbers.some((num) => num && num.length > 0);
      const hasMlsInfo =
        t &&
        t.mlsInfo &&
        Array.isArray(t.mlsInfo) &&
        t.mlsInfo.length > 0 &&
        t.mlsInfo.some((info) => info?.mlsNumber && info.mlsNumber.length > 0);
      return !hasMainMls && !hasMlsNumbers && !hasMlsInfo;
    };

    // Helper to check if legal description is missing
    const isLegalDescriptionMissing = (t) =>  !t.propertyDetails?.legalDescription || t.propertyDetails.legalDescription.trim().length === 0;

    // Helper to check if contract date is missing
    const isContractDateMissing = (t) => !t.contractDateTime;

    // Helper to check if closing date is missing
    const isClosingDateMissing = (t) => !t.closingDateTime;

    // Helper to check if sales price is missing
    const isSalesPriceMissing = (t) => !t.salesPrice || t.salesPrice.toString().trim().length === 0;

    // Helper to check if transaction is under contract
    const isUnderContract = (t) => t.status === "Under Contract";

    // Generate suggestions for all active transactions
    const suggestionEntries = [];
    filteredTransactions.forEach((t) => {
      if (t.agentRepresents === "Seller") {
        if (isMlsMissing(t)) {
          suggestionEntries.push({ transaction: t, issue: "Missing MLS #" });
        }
        if (isLegalDescriptionMissing(t)) {
          suggestionEntries.push({ transaction: t, issue: "Missing Legal Description" });
        }
      }

      if (isUnderContract(t)) {
        if (isContractDateMissing(t)) {
          suggestionEntries.push({ transaction: t, issue: "Missing Contract Date" });
        }
        if (isClosingDateMissing(t)) {
          suggestionEntries.push({ transaction: t, issue: "Missing Closing Date" });
        }
        if (isSalesPriceMissing(t)) {
          suggestionEntries.push({ transaction: t, issue: "Missing Sales Price" });
        }
      }
    });

    const weeklyDeadlineSuggestions = shouldShowWeeklyDeadlineSuggestions(currentUserProfile)
      ? buildWeeklyDeadlineSuggestions({
          transActive: transactions || [],
          tasksAllWithDateUpcoming: tasksAllWithDateUpcoming || [],
        })
      : [];

    const toDisplayDate = (value) => {
      if (!value) return null;
      const date = value?.toDate ? value.toDate() : new Date(value);
      return Number.isNaN(date.getTime()) ? null : format(date, "MMM d");
    };

    const getTransactionStatusLabel = (transaction) => {
      if (transaction.status === "Under Contract") return "Under Contract";
      return transaction.agentRepresents === "Buyer" ? "Active Buyer" : "Active Seller";
    };

    const getAddressLine = (transaction) => {
      if (transaction?.address?.street) {
        return convertAddressFull(transaction.address);
      }
      return transaction?.title || "No address available";
    };

    const getNotificationTitle = (entry) => {
      const docContext = entry.docName || entry.name || entry.message || null;
      const personContext = [entry.partyFirstName, entry.partyLastName].filter(Boolean).join(" ").trim();

      if (entry.type === "unresolvedComments" || entry._isDocumentComment) {
        return "Unresolved Comments";
      }
      if (entry.type === "formReviewed") {
        return "Form Reviewed";
      }
      if (entry.type === "signatureRequired") {
        return docContext ? `Signature Required ${docContext}` : "Signature Required";
      }
      if (entry.message) {
        return entry.message;
      }
      if (docContext) {
        return docContext;
      }
      if (personContext) {
        return `Update from ${personContext}`;
      }
      if (entry.type) {
        return entry.type
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (char) => char.toUpperCase());
      }
      return "Action required";
    };

    const getNotificationSubtitle = (entry) => {
      if (entry.type === "unresolvedComments" || entry._isDocumentComment) {
        return entry.docName || entry.name || null;
      }
      if (entry.docName && entry.docName !== entry.message) {
        return entry.docName;
      }
      if (entry.name && entry.name !== entry.message) {
        return entry.name;
      }
      if (entry.message && entry.message !== entry.docName) {
        return entry.message;
      }
      if (entry.transactionAddress) {
        return entry.transactionAddress;
      }
      return null;
    };

    const buildActionItems = (transaction, suggestionEntries) => {
      const items = [];
      const seen = new Set();

      suggestionEntries
        .filter((entry) => entry.transaction?.id === transaction.id)
        .forEach((entry) => {
          if (!seen.has(entry.issue)) {
            items.push({
              id: `${transaction.id}-${entry.issue}`,
              title: entry.issue,
              subtitle: null,
              type: "suggestion",
            });
            seen.add(entry.issue);
          }
        });

      weeklyDeadlineSuggestions
        .filter((entry) => entry.transactionId === transaction.id)
        .forEach((entry) => {
          const key = `${transaction.id}-weekly`;
          if (!seen.has(key)) {
            items.push({ id: key, title: "Upcoming deadlines this week", subtitle: null, type: "weekly" });
            seen.add(key);
          }
        });

      [...notifications, ...docsWithComments]
        .filter((entry) => entry.transactionId === transaction.id || entry.transaction?.id === transaction.id)
        .forEach((entry) => {
          const title = getNotificationTitle(entry);
          const subtitle = getNotificationSubtitle(entry);
          const actionType = entry.type === "unresolvedComments" || entry._isDocumentComment
            ? "unresolvedComments"
            : entry.type || "notification";

          const docId = entry.docId || entry.id;
          const key = actionType === "unresolvedComments"
            ? `${transaction.id}-unresolvedComments-${docId}`
            : `${transaction.id}-${entry.id || title}`;
          if (!seen.has(key)) {
            items.push({ id: key, title, subtitle, type: actionType, docId });
            seen.add(key);
          }
        });

      return items;
    };

    const buildCardData = (transactions, suggestionEntries) => {
      return transactions
        .map((transaction) => ({
          transaction,
          actionItems: buildActionItems(transaction, suggestionEntries),
        }))
        .filter(({ actionItems }) => actionItems.length > 0)
        .sort((a, b) => {
          const aDate = a.transaction.closingDateTime?.toDate?.() || a.transaction.closingDateTime;
          const bDate = b.transaction.closingDateTime?.toDate?.() || b.transaction.closingDateTime;
          if (!aDate && !bDate) return 0;
          if (!aDate) return 1;
          if (!bDate) return -1;
          return new Date(aDate) - new Date(bDate);
        });
    };

    const cardData = buildCardData(filteredTransactions, suggestionEntries);

    const renderSuggestionCard = (card) => {
      const { transaction, actionItems } = card;
      const isExpanded = expandedTransactionId === transaction.id;
      const visibleItems = isExpanded ? actionItems : actionItems.slice(0, 5);
      const hasMore = actionItems.length > 5;

      const handleActionClick = (item) => {
        if (item.type === "suggestion") {
          dispatch(
            openModal({
              modalType: "TransactionEditForm",
              modalProps: { transaction },
            })
          );
          return;
        }

        if (item.type === "signatureRequired") {
          if (transaction.id) {
            const docId = item.docId;
            const targetPath = docId
              ? `/transactions/${transaction.id}/documents/${docId}/view`
              : `/transactions/${transaction.id}/documents`;
            navigate(targetPath, { state: { openAgentSignModal: true } });
          }
          return;
        }

        if (item.type === "formReviewed") {
          if (transaction.id) {
            const docId = item.docId;
            const targetPath = docId
              ? `/transactions/${transaction.id}/documents/${docId}/prepare`
              : `/transactions/${transaction.id}/documents`;
            navigate(targetPath);
          }
          return;
        }

        if (item.type === "unresolvedComments") {
          if (transaction.id) {
            const docId = item.docId;
            const targetPath = docId
              ? `/transactions/${transaction.id}/documents/${docId}/view`
              : `/transactions/${transaction.id}/documents`;
            navigate(targetPath, { state: { showComments: true } });
          }
          return;
        }

        if (transaction.id) {
          goToTransaction(transaction.id);
        }
      };

      return (
        <div key={transaction.id} style={{ width: isMobile ? "100%" : "calc(33.333% - 8px)", minWidth: 0 }}>
          <Card style={{ width: "100%", minHeight: "100%" }}>
          <Card.Content>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <Card.Header style={{ marginBottom: "4px" }}>
                  {transaction.title}
                </Card.Header>
                <Card.Meta style={{ color: "#555", marginBottom: "4px" }}>
                  {getAddressLine(transaction)}
                </Card.Meta>
                <Card.Description>
                  <div style={{ color: "#2185d0", fontWeight: 600 }}>
                    {getTransactionStatusLabel(transaction)}
                    {transaction.closingDateTime && (
                      <span style={{ color: "#777", fontWeight: 400 }}>
                        {" | Closing: "}{toDisplayDate(transaction.closingDateTime)}
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: "8px", fontWeight: 600 }}>
                    Needs Action:
                  </div>
                  <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "6px" }}>
                    {visibleItems.length > 0 ? (
                      visibleItems.map((item) => {
                        let buttonLabel = "View Transaction";
                        if (item.type === "suggestion") buttonLabel = "Edit";
                        if (item.type === "signatureRequired") buttonLabel = "Sign Document";
                        if (item.type === "formReviewed") buttonLabel = "Prepare Signatures";
                        if (item.type === "unresolvedComments") buttonLabel = "View Comments";

                        const itemIcon = item.type === "unresolvedComments"
                          ? "comment outline"
                          : item.type === "weekly"
                            ? "bell outline"
                            : item.type === "signatureRequired"
                              ? "edit outline"
                              : item.type === "formReviewed"
                                ? "check circle outline"
                                : "warning circle";

                        return (
                          <div key={item.id} style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "6px" }}>
                                <Icon name={itemIcon} size="small" color={item.type === "unresolvedComments" ? "orange" : "blue"} />
                                <div style={{ fontSize: "0.9em", color: "#444", fontWeight: 600 }}>
                                  {item.title}
                                </div>
                              </div>
                              <Button
                                basic
                                size="tiny"
                                compact
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleActionClick(item);
                                }}
                              >
                                {buttonLabel}
                              </Button>
                            </div>
                            {item.subtitle && (
                              <div style={{ fontSize: "0.8em", color: "#777", marginLeft: "22px" }}>
                                {item.subtitle}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <span style={{ color: "#888" }}>No action items</span>
                    )}
                  </div>
                </Card.Description>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px" }}>
                <Dropdown
                  icon="ellipsis vertical"
                  floating
                  button
                  className="icon"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Dropdown.Menu direction="left">
                    <Dropdown.Item
                      icon="edit"
                      text="Edit Transaction"
                      onClick={(event) => {
                        event.stopPropagation();
                        dispatch(
                          openModal({
                            modalType: "TransactionEditForm",
                            modalProps: { transaction },
                          })
                        );
                      }}
                    />
                    <Dropdown.Item
                      icon="eye"
                      text="View Transaction"
                      onClick={(event) => {
                        event.stopPropagation();
                        goToTransaction(transaction.id);
                      }}
                    />
                  </Dropdown.Menu>
                </Dropdown>
                {hasMore && (
                  <Button
                    basic
                    size="mini"
                    onClick={(event) => {
                      event.stopPropagation();
                      setExpandedTransactionId(isExpanded ? null : transaction.id);
                    }}
                  >
                    {isExpanded ? "Hide" : "View All"}
                  </Button>
                )}
              </div>
            </div>
          </Card.Content>
          </Card>
        </div>
      );
    };

    const renderSuggestionCards = (cards) => (
      <div style={{ marginBottom: "24px" }}>
        {cards.length === 0 ? (
          <p style={{ color: "#888" }}>No suggestions at the moment.</p>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            {cards.map((card) => renderSuggestionCard(card))}
          </div>
        )}
      </div>
    );

    return (
      <div style={{ textAlign: "left", padding: isMobile ? "10px" : "10px" }}>
        <h2>Suggestions</h2>
        <p>These are suggestions for missing information in your active and under contract transactions.
         <br/> Cards are ordered by the soonest closing date and expand to show all action items.
        </p>
        <Grid stackable>
          <Grid.Column computer={16}>
            {renderSuggestionCards(cardData)}
          </Grid.Column>
        </Grid>
      </div>
    );
  };

  return (
    <div className="main-page-wrapper">
      <Grid stackable>
        <Grid.Column computer={16}>
          <h1>Assist Dashboard</h1>
        </Grid.Column>
        <Grid.Column computer={16} style={{ marginBottom: "20px" }}>
          <Button.Group>
            <Button
              color={activeView === "Tasks" ? "blue" : undefined}
              onClick={() => setActiveView("Tasks")}
            >
              Tasks
            </Button>
            <Button
              color={activeView === "Milestones" ? "blue" : undefined}
              onClick={() => setActiveView("Milestones")}
            >
              Milestones
            </Button>
            <Button
              color={activeView === "Suggestions" ? "blue" : undefined}
              onClick={() => setActiveView("Suggestions")}
            >
              Suggestions
            </Button>
          </Button.Group>
        </Grid.Column>
        <Grid.Column computer={16}>
          {activeView === "Tasks" && renderTasksView()}
          {activeView === "Milestones" && renderMilestonesView()}
          {activeView === "Suggestions" && renderSuggestionsView()}
        </Grid.Column>
      </Grid>
    </div>
  );
}


